import {
  AnalyticsActionLoginEvent,
  AnalyticsActionLogoutEvent,
  AnalyticsEventParam,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { proxy, snapshot, useSnapshot } from 'valtio';

import { userService } from '#/client/api';
import {
  AuthenticationType,
  Entitlements,
  Profile,
  ProfileResponse,
  TokenResponse,
} from '#/client/models';
import { devtoolsApi } from '#/dev/devtools';
import { CoreAuthFeature } from '#/service/config/features.types';
import { StorageKeys } from '#/service/constants';
import { LS } from '#/service/storage';
import { falsyToNull, sleep } from '#/service/utils';

import { resetStore } from '../common';
import {
  canManageSubscription,
  getAnonymousToken,
  getEntitlementsKey,
  getShouldPromptPreferencesFromLS,
  guessInitialLoginFromLS,
  saveAuthToken,
  setShouldPromptPreferencesInLS,
} from './utils';

export interface AuthStore {
  /** is standard or TVE login enabled based on core authentication config */
  isAuthEnabled: boolean;
  isInitialised: boolean;
  /** is user logged in */
  isLoggedIn: boolean;
  /** user has subscription or not */
  isEntitled: boolean;
  /** expiration date of subscription */
  expirationDate: string;
  type: AuthenticationType;
  /**
   * It's effectively the same as `isWeb` flag in subscription details, but this is
   * extracted from `/profile` response data. It determines if user can manage subscription
   * on Web platform
   * */
  canManageSubscription: boolean;
  profile: Profile;
  entitlements: Entitlements;
  /** is login modal open */
  isLoginShown: boolean;
  /** is standard login type enabled in config */
  isStandardLogin: boolean;
  /** is standard login related forgot password feature enabled in core authentication config */
  isForgotPasswordEnabled: boolean;
  /** is standard login related registration feature enabled in core authentication config */
  isRegistrationEnabled: boolean;
  /** is second screen login feature enabled in core authentication config */
  isSecondScreen: boolean;
  /** is TVE login feature enabled in core authentication config */
  isTve: boolean;
  /** Is user profile loading */
  isProfileLoading: boolean;
  userId?: string;
  /** The complete profile response as we get it from Core. We only use this to pass it into the contexts. */
  profileResponse?: ProfileResponse;
  /** authentication config from core */
  config?: CoreAuthFeature;
  sessionId?: string;
  /** Cache key of user entitlements. Key updates on every profile fetch */
  entitlementsKey?: string;
  /** Used only after registration succeeded to track if we have entitlements ready */
  awaitingEntitlements?: boolean;
  /** Used to determine if we should show preferences prompt to the user */
  shouldPromptPreferences: boolean;
  /** Redirect URL used after a successful second screen login. Falsy means go to home page. */
  secondScreenRedirectUrl?: string;
}

/**
 * Object representing Auth state.
 * Please DO NOT use it directly in the app's code (tests and other stores are exception here).
 * Instead, you should use:
 * @example const { ... } = useAuthStore();
 **/

export const authStore = proxy<AuthStore>({
  isAuthEnabled: false,
  isInitialised: false,
  isLoggedIn: guessInitialLoginFromLS(),
  isEntitled: false,
  expirationDate: '',
  type: AuthenticationType.None,
  canManageSubscription: false,
  profile: {
    attributes: {},
  },
  entitlements: {
    subscriber: false,
    grantedEntitlementTags: [],
  },
  isLoginShown: false,
  isStandardLogin: false,
  isForgotPasswordEnabled: false,
  isRegistrationEnabled: false,
  isSecondScreen: false,
  isTve: false,
  isProfileLoading: false,
  userId: undefined,
  profileResponse: undefined,
  config: undefined,
  sessionId: crypto.randomUUID(),
  entitlementsKey: undefined,
  awaitingEntitlements: undefined,
  shouldPromptPreferences: getShouldPromptPreferencesFromLS(),
  secondScreenRedirectUrl: undefined,
});
export const initialAuthStore = snapshot(authStore) as AuthStore;

/**
 * Function setting useful flags from authentication core config to auth store.
 *
 * @config: core authentication config
 **/
const setAuthenticationFeatures = (config: CoreAuthFeature) => {
  authStore.isAuthEnabled =
    !!config?.standard?.enabled || !!config?.tve?.enabled;
  authStore.isStandardLogin = !!config?.standard?.enabled;
  authStore.isForgotPasswordEnabled =
    !!config?.standard?.forgotPassword?.enabled;
  authStore.isRegistrationEnabled = !!config?.standard?.registration?.enabled;
  authStore.isTve = !!config?.tve?.enabled;
  authStore.isSecondScreen = !!config?.secondScreen?.enabled;
};

// 3.1 seconds, because MPP have up to 15s timeout. I saw in the testing wait times
// of 6s or 8s in the most scenarios, so 3.1s is a safe bet (0.1s for safety to not
// land in another window)
const RETRY_AUTHPROFILE_TIMEOUT_MS = 3100;
export const MAX_AUTHPROFILE_RETRIES = 5;

export const authActions = {
  updateProfile(profile: ProfileResponse) {
    authStore.profileResponse = profile;
    authStore.type = profile.authentication.type;
    authStore.userId = profile.userId;
    authStore.profile = profile.profile;
    authStore.entitlements = profile.entitlements;

    authStore.sessionId ??= crypto.randomUUID();

    authStore.entitlements.grantedEntitlementTags ||= [];

    authStore.isLoggedIn = !!authStore.userId;
    authStore.isEntitled =
      authStore.entitlements.grantedEntitlementTags.length > 0;
    authStore.expirationDate = profile.authentication.expiration;
    authStore.canManageSubscription = canManageSubscription(profile);
    authStore.entitlementsKey = getEntitlementsKey(profile);
  },

  setProfileAttribute(fieldName: string, fieldValue: any) {
    authStore.profile.attributes[fieldName] = fieldValue;
  },

  setProfilePreferences(fieldName: string, fieldValue: any) {
    authStore.profile.preferences ||= {};
    authStore.profile.preferences[fieldName] = fieldValue;
  },

  setShouldPromptPreferences(value?: boolean) {
    if (value === undefined) {
      return;
    }

    authStore.shouldPromptPreferences = value;
    setShouldPromptPreferencesInLS(value);
  },

  /** Show login modal */
  showLogin() {
    authStore.isLoginShown = true;
  },

  /** Hide login modal */
  hideLogin() {
    authStore.isLoginShown = false;
  },

  setConfig(config: CoreAuthFeature) {
    authStore.config = config;

    setAuthenticationFeatures(config);
  },

  setSecondScreenRedirectUrl(redirectUrl: string | undefined) {
    authStore.secondScreenRedirectUrl = redirectUrl;
  },

  /** Logs user out by executing all necessary things like:
   * - updating store
   * - firing necessary analytics events
   * - removing saved data from local storage
   */
  async logout(dispatchAnalytics = true) {
    if (dispatchAnalytics && authStore.isAuthEnabled) {
      document.dispatchEvent(
        new AnalyticsActionLogoutEvent({
          [AnalyticsEventParam.UserId]: authStore.userId,
          [AnalyticsEventParam.SessionId]: authStore.sessionId ?? '',
          [AnalyticsEventParam.UserProfile]: authStore.profile,
        })
      );
    }

    await getAnonymousToken();

    await logout();
    LS.remove(StorageKeys.AUTHENTICATION);
    authActions.setSecondScreenRedirectUrl(undefined);
  },

  /**
   * When you authenticated user correctly use this method to update
   * store accordingly and kick off necessary analytic events
   **/
  async login(
    tokenResponse: TokenResponse,
    screenType?: ScreenType,
    pageHeading?: string
  ) {
    const profile = await authActions.refreshProfile();
    saveAuthToken(tokenResponse);
    authActions.setShouldPromptPreferences(tokenResponse.preferences?.prompt);

    document.dispatchEvent(
      new AnalyticsActionLoginEvent({
        [AnalyticsEventParam.UserId]: authStore.userId,
        [AnalyticsEventParam.UserProfile]: authStore.profile,
        [AnalyticsEventParam.UserEntitlements]: authStore.entitlements,
        [AnalyticsEventParam.SessionId]: authStore.sessionId,
        [AnalyticsEventParam.ScreenType]: screenType,
        [AnalyticsEventParam.ScreenHeading]: falsyToNull(pageHeading),
      })
    );

    authActions.hideLogin();

    return profile;
  },

  async refreshProfile() {
    authStore.isProfileLoading = true;

    try {
      const profileRes = await userService.getProfile();
      const profile = profileRes.data;
      authStore.isProfileLoading = false;
      authActions.updateProfile(profile);

      return {
        userId: authStore.userId,
        sessionId: authStore.sessionId,
        errors: profile._meta.errors,
        subscribed: profile.subscriber ?? false,
      };
    } catch {
      authStore.isProfileLoading = false;

      return {
        userId: authStore.userId,
        sessionId: authStore.sessionId,
        subscribed: false,
      };
    }
  },

  /**
   * This is pretty much the same as refreshProfile action.
   * The difference is that this method is meant to be used after successful purchase of a product.
   * We and user expects that after product purchase he can watch content immediately.
   * It will run the standard refreshProfile action and in case entitlements are still not granted,
   * it will re-try X times. Reason is that it may take for the backend up to 15s to process the purchase.
   **/
  async refreshProfileAfterEntitlementPurchase() {
    authStore.awaitingEntitlements = true;
    const originalEntitlementsKey = authStore.entitlementsKey;

    let profile = await authActions.refreshProfile();

    if (authStore.entitlementsKey !== originalEntitlementsKey) {
      authStore.awaitingEntitlements = false;

      return profile;
    }

    for (let i = 0; i < MAX_AUTHPROFILE_RETRIES; i++) {
      await sleep(RETRY_AUTHPROFILE_TIMEOUT_MS);

      profile = await authActions.refreshProfile();

      if (authStore.entitlementsKey !== originalEntitlementsKey) {
        authStore.awaitingEntitlements = false;

        return profile;
      }
    }

    authStore.awaitingEntitlements = false;

    console.warn(
      `Entitlements were not granted after ${MAX_AUTHPROFILE_RETRIES} retries.`
    );

    return profile;
  },

  reset() {
    resetStore(authStore, initialAuthStore);
  },
};

export const useAuthStore = () => useSnapshot(authStore) as AuthStore;

export async function initAuth() {
  // if the user is signed in on initial load, we refresh the users profile.
  if (authStore.isLoggedIn) {
    await authActions.refreshProfile();
  }

  // if the user is not logged in on initial load, we get an anonymous token.
  // if we already have a anonymous user, we don't request a new token.
  if (authStore.type === AuthenticationType.None) {
    await getAnonymousToken();

    authStore.type = AuthenticationType.Anonymous;
    authStore.sessionId ??= crypto.randomUUID();
  }

  // now we have a token, either authenticated or anonymous.
  authStore.isInitialised = true;
}

async function logout() {
  authStore.isLoggedIn = false;
  authStore.isEntitled = false;
  authStore.expirationDate = '';

  authStore.type = AuthenticationType.None;
  authStore.userId = undefined;
  authStore.sessionId = undefined;
  authStore.profile = {
    attributes: {},
    preferences: {},
  };
  authStore.profileResponse = undefined;
  authStore.entitlements = {
    grantedEntitlementTags: [],
    subscriber: false,
  };
  authActions.setShouldPromptPreferences(false);

  await authActions.refreshProfile();
}

devtoolsApi.setStore('auth', authStore, authActions);
